<template>
  <div class="table-responsive">
    <table class="table">
      <thead>
        <tr>
          <th>Signup Date</th>
          <th>Group Name / Main Contact</th>
          <th>Group GA</th>
          <th>Location</th>
          <th>Est. # of Employees / Industry</th>
          <th>Totals</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <!-- First load: show quick buttons inside the table -->
        <tr v-if="isInitialLoad">
          <td colspan="7" class="empty-state-cell">
            <div class="quick-date-btns-inline d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3">
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'all' }"
                @click="$emit('quick-filter', 'all')"
                title="All groups"
              >
                <i class="fas fa-list me-1"></i>
                All Groups
              </button>
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'today' }"
                @click="$emit('quick-filter', 'today')"
                title="Groups signed up today"
              >
                <i class="fas fa-calendar-day me-1"></i>
                Groups Today
              </button>
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'week' }"
                @click="$emit('quick-filter', 'week')"
                title="Groups signed up this week"
              >
                <i class="fas fa-calendar-week me-1"></i>
                Groups This Week
              </button>
              <button
                type="button"
                class="quick-date-btn-inline"
                :class="{ active: activeQuickFilter === 'month' }"
                @click="$emit('quick-filter', 'month')"
                title="Groups signed up this month"
              >
                <i class="fas fa-calendar-alt me-1"></i>
                Groups This Month
              </button>
            </div>
            <div class="empty-state-msg">
              <i class="fas fa-search me-2"></i>
              Enter search criteria or use a quick search above to display results.
            </div>
          </td>
        </tr>

        <!-- After search: no results -->
        <tr v-else-if="!rows.records || !rows.records.length">
          <td colspan="7" align="center">No records found.</td>
        </tr>

        <!-- Data rows -->
        <tr
          v-else
          v-for="(row, index) in rows.records"
          :key="index"
          :class="row.group_status.toLowerCase() == 'active' ? '' : 'inactive-row'"
        >
          <td>
            <span>{{ row.signup_date }}</span>
            <span
              :class="
                row.group_status.toLowerCase() == 'active'
                  ? 'id-status-active'
                  : 'id-status-inactive'
              "
              >{{ row.group_status }}</span
            >
            <span><strong>Code:</strong> {{ row.group_code }}</span>
          </td>
          <td>
            <h3>{{ row.group_name }}</h3>
            <span>{{ row.group_contact_full_name }}</span>
            <span>
              <strong>Type:</strong>
              {{ row.group_type }}
            </span>
            <span>
              <strong>Email:</strong>
              {{ row.group_email }}
              <valid-icon
                :is-valid="row.is_email_valid"
                className="valid-icon"
              />
            </span>
            <span>
              <strong>Phone:</strong>
              {{ formatGroupPhone(row.group_phone) }}
              <valid-icon
                v-if="formatGroupPhone(row.group_phone) != 'N/A'"
                :is-valid="row.is_phone_valid"
                className="valid-icon"
              />
            </span>
            <span>
              <strong>Web Url:</strong>
              {{ row.group_web ? "/" + row.group_web : "N/A" }}
            </span>
            <span v-if="row.is_plans_assigned" class="mt-2">
              <b> Plans Assigned </b>
              <img
                src="@/assets/images/approved.png"
                alt="Plans Assigned"
                style="width: 12px; height: 12px; margin-left: 4px"
              />
            </span>
          </td>
          <td>
            <span>
              <strong>
                {{
                  row.group_ga_agent_full_name
                    ? row.group_ga_agent_full_name
                    : "N/A"
                }}
              </strong>
            </span>
            <span>
              {{ row.group_ga_agent_code ? row.group_ga_agent_code : "N/A" }}
            </span>
          </td>
          <td>{{ row.location }}</td>
          <td>
            <span>{{ row.estimate_number }}</span>
            <span><strong>Industry:</strong> {{ row.emp_industry }}</span>
          </td>
          <td>
            <ul>
              <li><strong>A:</strong> {{ row.active_total }}</li>
              <li><strong>W:</strong> {{ row.withdraw_total }}</li>
              <li><strong>T:</strong> {{ row.termed_total }}</li>
              <li>
                <strong>Totals:</strong>
                {{
                  getStatTotal(
                    row.active_total,
                    row.withdraw_total,
                    row.termed_total
                  )
                }}
              </li>
            </ul>

            <template
              v-if="
                getStatTotal(
                  row.active_total,
                  row.withdraw_total,
                  row.termed_total
                ) > 0
              "
            >
              <router-link
                :to="{
                  name: 'MemberListByGroup',
                  params: { groupId: encodedId(row.group_id) },
                }"
                target="_blank"
                class="viewClient-btn"
              >
                View Clients
              </router-link>
            </template>
          </td>
          <td class="action-btn-td">
            <div class="action-btn">
              <custom-route-button
                :routeLink="{
                  name: 'EmployerGroupPlans',
                  params: { groupId: encodedId(row.group_id) },
                }"
                title="Assign Plan"
                iconClass="fas fa-plus"
              />
              <!-- <router-link
                to="/group-rep-display-setting"
                v-b-tooltip.hover
                title="Display Setting"
                ><i class="fas fa-desktop"></i
              ></router-link> -->
              <custom-route-button
                :routeLink="{
                  name: 'EmployerGroupDetail',
                  params: { groupId: encodedId(row.group_id) },
                }"
                title="View/Edit Details"
                iconClass="fas fa-eye"
              />

              <template v-if="row.active_total + row.termed_total > 0">
                <custom-button
                  title="Payment History"
                  iconClass="fas fa-dollar-sign"
                  @onClicked="openModal(row, 'payment-history-modal')"
                />
                <custom-button
                  title="View Invoice"
                  iconClass="fas fa-file-invoice-dollar"
                  @onClicked="openModal(row, 'download-invoice-modal')"
                />
              </template>
              <custom-button
                title="Assign Platforms"
                iconClass="fa-solid fa-handshake"
                @onClicked="openModal(row, 'view-domain-modal')"
              />
              <custom-button
                title="Benefit Store"
                iconClass="fas fa-shopping-cart"
                @onClicked="toBenefitStore(row.group_id)"
              />
              <custom-button
                v-if="row.actions.show_default_otp"
                title="View Default OTP"
                iconClass="fas fa-list"
                @onClicked="openModal(row, 'defaultOtpModal')"
              />

              <custom-button
                title="Send Group Enrollment"
                iconClass="fas fa-envelope"
                @onClicked="openModal(row, 'send-group-enrollment-modal')"
              />
              <button
                title="View msg"
                class="custom-btn m-0"
                v-b-tooltip.hover="ViewMsg"
                @click="routeToViewMessage(row)"
              >
                <img src="../../../assets/images/view-msg.svg" />
              </button>
              <custom-button
                title="View Message"
                :imageSrc="require('@/assets/images/view-msg.svg')"
                @onClicked="routeToViewMessage(row)"
              />

               <custom-button v-if="isVipUser && row.is_switchable"
                title="Switch To Group Account"
                iconClass="fas fa-exchange-alt"
                @onClicked="switchToGroupRequest(row)"
              />

                <custom-button
                v-if="row.actions.show_waive_button"
                title="Waive Fee"
                iconClass="fas fa-money-bill-wave"
                @onClicked="openWaiveFeeModal(row)"
              />
              <custom-button
                v-if="row.actions.show_waive_button"
                title="Waive Fee Logs"
                iconClass="fas fa-money-check-dollar"
                @onClicked="openWaiveEfeeLogs(row)"
              />
              <a
                v-if="isVipUser && row.is_switchable"
                href="javascript:void(0);"
                @click="openLogModal(row.group_id)"
                v-b-tooltip.hover
                title="Switch Logs"
              >
                <img src="@/assets/images/rep-logs.svg" width="26px" alt="" />
              </a>
              <a
                href="javascript:void(0);"
                v-b-tooltip.hover
                title="Add Notes"
                @click="openAddNotesModal(row)"
              >
                <img src="@/assets/images/add-notes-rounded.svg" width="16px" alt="Add Employers/Group Notes" />
              </a>
              <a
                href="javascript:void(0);"
                v-b-tooltip.hover
                title="View Notes"
                @click="openViewNotesModal(row)"
              >
                <img src="@/assets/images/notes-bold.svg" width="16px" alt="View Employers/Group Notes" />
              </a>
              <custom-button v-if="isVipUser && row.is_switchable"
                title="Switch To Group Account"
                iconClass="fas fa-exchange-alt"
                @onClicked="switchToGroupRequest(row)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <payment-history-modal :row="row" @onCloseModal="row = null" />
    <download-invoice-modal :row="row" @onCloseModal="row = null" />
    <default-otp-modal :row="row" rowType="G" @onCloseModal="row = null" />
    <send-group-enrollment :row="row" @onCloseModal="row = null" />
    <view-domain-modal
      :row="row"
      :allDomains="this.allDomains"
      @onCloseModal="row = null"
    />
     <WaiveEfeeModal
      :show="showWaiveFeeModal"
      :row="row"
      @onCloseModal="closeWaiveEfeeModal"
      @onSuccess="$emit('refreshList')"
    />
    <WaiveEfeeLogs
      :show="showWaiveEfeeLogs"
      :row="row"
      :type="'group'"
      @onCloseModal="closeWaiveEfeeLogs"
    />
    <LogDisplayModal
      :isOpen="isLogModalOpen"
      @update:isOpen="isLogModalOpen = $event"
      :logData="switchLogData"
      :userId="selectedGroupId"
      :logType="'group'"
    />
    <add-notes-modal
      :row="row"
      :isOpen="showAddNotesModal"
      @close="showAddNotesModal = false"
    />
    <view-notes-modal
      :row="row"
      :isOpen="showViewNotesModal"
      @close="showViewNotesModal = false"
    />
  </div>
</template>
<script>
import Helper from "@/resource/Helper";
import PaymentHistoryModal from "../modals/PaymentHistoryModal.vue";
import DownloadInvoiceModal from "../modals/DownloadInvoiceModal.vue";
import ViewDomainModal from "../modals/ViewDomainModal.vue";
import DefaultOtpModal from "@/components/ui/DefaultOtpModal.vue";
import SendGroupEnrollment from "../modals/SendGroupEnrollment.vue";
import WaiveEfeeModal from "../../MemberList/modal/WaiveEfeeModal.vue";
import WaiveEfeeLogs from "../../MemberList/modal/WaiveEfeeLogs.vue";
import LogDisplayModal from "../../MemberList/modal/LogDisplayModal.vue";
import Api from "@/resource/Api";
import AddNotesModal from '../modals/AddNotesModal.vue';
import ViewNotesModal from '../modals/ViewNotesModal.vue';

export default {
  name: "EmployerGroupListTable",
  components: {
    PaymentHistoryModal,
    ViewDomainModal,
    DownloadInvoiceModal,
    DefaultOtpModal,
    SendGroupEnrollment,
    WaiveEfeeModal,
    WaiveEfeeLogs,
    LogDisplayModal,
    AddNotesModal,
    ViewNotesModal
  },
  props: {
    rows: {
      required: true,
    },
    allDomains: {
      type: Array,
    },
    isInitialLoad: {
      type: Boolean,
      default: true,
    },
    activeQuickFilter: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      row: null,
      showWaiveFeeModal: false,
      showWaiveEfeeLogs: false,
      isLogModalOpen: false,
      switchLogData: {},
      selectedGroupId: null,
      showAddNotesModal: false,
      showViewNotesModal: false,
    };
  },
  computed: {
    isVipUser() {
      return this.$store.getters.vipUser;
    }
  },
  methods: {
    encodedId(id) {
      return btoa(id);
    },
    getStatTotal(activeTotal, withdrawTotal, termedTotal) {
      let total = 0;
      total =
        parseInt(activeTotal) + parseInt(withdrawTotal) + parseInt(termedTotal);
      return total;
    },
    formatGroupPhone(phone) {
      return Helper.formatPhoneNumber(phone)
        ? Helper.formatPhoneNumber(phone)
        : "N/A";
    },
    openModal(row, modalId) {
      this.row = row;
      this.$bvModal.show(modalId);
    },

    openWaiveFeeModal(row) {
      this.row = row;
      this.showWaiveFeeModal = true;
    },
    closeWaiveEfeeModal() {
      this.row = null;
      this.showWaiveFeeModal = false;
    },
    openWaiveEfeeLogs(row) {
      this.row = row;
      this.showWaiveEfeeLogs = true;
    },
    closeWaiveEfeeLogs() {
      this.row = null;
      this.showWaiveEfeeLogs = false;
    },
    openLogModal(groupId=null) {
      this.selectedGroupId = groupId;
      let url = 'v1/switch-to-group';
      if(this.isVipUser && groupId) {
        url += '?group_id=' + groupId;
      }
      const loader = this.$loading.show();
      Api.get(url).then(({ data }) => {
          this.switchLogData = data;
          this.isLogModalOpen = true;
          loader.hide();
      }).catch((err) => {
          console.log(err);
          this.$notify("Something went wrong.Please Try Again.","error")
          loader.hide();
      })
    },
    closeLogModal() {
      this.isLogModalOpen = false;
    },
    routeToViewMessage(row) {
      let app = this;
      let userInfo = {
        user_id: "",
        user_message_type: "",
      };
      userInfo.user_id = row.group_id;
      userInfo.user_message_type = "G";
      app.$store.commit("setViewMessageUserInfo", userInfo);
      const routePath = app.$router.resolve("/view-msg").href;
      window.open(routePath, "_blank");
    },
    toBenefitStore(id) {
      let url = `${this.$endpoint.BENEFIT_STORE_URL}/group-home?group_id=${btoa(
        id
      )}`;
      window.open(url, "_blank");
    },
    switchToGroupRequest(item) {
      let loader= this.$loading.show();
      let data= {
        "group_id":item.group_id,
        "group_email":item.group_email,
        "deviceid":this.$store.getters.deviceId,
        "action_type":"switch_to_group"
      }
      Api.post(`v1/switch-to-group`, data).then((res)=>{
        const {switch_id} = res.data;
         this.$notify(
          `${res.data?.message}. Please Wait For Redirection.`,
          "success"
        )
        setTimeout(()=>{
          window.open(`${Helper.groupDashboard()}?switch-group=${switch_id}`, '_blank');
        },2000);
        
        loader.hide();
      }).catch((e)=>{
        this.$notify("Something went wrong.Please Try Again.","error")
        console.log(e)
        loader.hide();
      })
    },
    openAddNotesModal(row) {
      this.row=row;
      this.showAddNotesModal = true;
      this.$bvModal.show("addGroupNotesModal");
    },
    openViewNotesModal(row) {
      this.row=row;
      this.showViewNotesModal = true;
      this.$bvModal.show("viewGroupNotesModal"); 
    },
  },
};
</script>
<style scoped>
.valid-icon {
  width: 2.8%;
}
.action-btn {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 1rem;
}
.empty-state-cell {
  padding: 2.5rem 1rem;
  text-align: center;
}
.empty-state-msg {
  color: #3BC6F2;
  font-size: 14px;
  font-style: italic;
  font-weight: bold;
  display: inline-block;
}
.quick-date-btns-inline {
  gap: 8px;
}
.quick-date-btn-inline {
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 6px;
  border: 1.5px solid #3BC6F2;
  background-color: #e8f7fd;
  color: #3BC6F2;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
  height: 36px;
  line-height: 1;
}
.quick-date-btn-inline:hover {
  background-color: #3BC6F2;
  border-color: #3BC6F2;
  color: #fff;
}
.quick-date-btn-inline.active {
  background-color: #3BC6F2;
  border-color: #3BC6F2;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 198, 242, 0.32);
}
@media (max-width: 1440px) {
  .action-btn-td {
    min-width: 200px;
  }
}
@media (max-width: 1024px) {
  .action-btn-td {
    min-width: 190px;
  }
}
@media (max-width: 768px) {
  .action-btn-td {
    min-width: 150px;
  }
  .quick-date-btn-inline {
    font-size: 12px;
    padding: 5px 10px;
  }
}
</style>
